import React from 'react';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import { useModal } from '@/components/modal/ModalProvider';

export default function Price({
  price,
  compareAtPrice,
  priceClassOverride,
  crossedPriceClassOverride,
  showSavings,
  darkPdp,
  pdpCrossSell,
  pricingDetailsData,
  pricingDetailsClass,
  variant,
  tagText,
  microsite
}) {
  const { showPriceDetails } = useModal();
  const pathname = useAppPathname();
  const parsedPrice = parseFloat(price);
  const parsedComparePrice = parseFloat(compareAtPrice);

  // Check if we're in a microsite feature context
  const isMicrositeFeature = microsite || pathname?.startsWith('/feature/');

  function handlePriceModalClick(event) {
    event.preventDefault();
    showPriceDetails(variant, null, pricingDetailsData);
  }

  const priceDifference = parsedComparePrice - parsedPrice;
  const amountSaved = formatPrice(priceDifference, false);
  const salePriceClassName = darkPdp
    ? 'price-l-form'
    : pdpCrossSell
      ? 'price-s-form'
      : priceClassOverride
        ? priceClassOverride
        : 'price-r';

  if (price === undefined) return null;

  // If we're in a microsite feature context, render the price with the tag and price details link
  if (isMicrositeFeature) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-wrap items-center gap-1">
          <span className={`${salePriceClassName} mr-1`}>
            {formatPrice(parsedPrice)}
          </span>
          <CrossedPrice
            value={parsedComparePrice}
            className={
              crossedPriceClassOverride
                ? crossedPriceClassOverride
                : 'price-r-crossed'
            }
          />
          {tagText && (
            <span className="badge-text-r border-light-green text-light-green items-center rounded-3xl border-1 px-5 py-1">
              {tagText}
            </span>
          )}
        </div>
        <div className="mt-1">
          {pricingDetailsData && (
            <div
              className={`text-dawn-grey-30 body-xs pdp-price-details cursor-pointer text-left underline`}
              onClick={handlePriceModalClick}
            >
              Price Details
            </div>
          )}
        </div>
      </div>
    );
  }

  // If we're not in a Microsite feature context
  // If the price is less than the compareAtPrice, show the sale price, crossed price, and savings
  if (parsedPrice < parsedComparePrice) {
    return (
      <span
        className={`flex items-baseline justify-between ${
          pricingDetailsClass?.includes('pdp-price-details') ? '' : ''
        }`}
      >
        {/* LEFT SIDE: Prices and savings */}
        <span className="flex items-baseline whitespace-nowrap">
          <SalePrice value={parsedPrice} className={salePriceClassName} />
          <CrossedPrice
            value={parsedComparePrice}
            className={
              crossedPriceClassOverride
                ? crossedPriceClassOverride
                : 'price-r-crossed'
            }
          />
          {showSavings && (
            <del className="price-saved ml-3 whitespace-nowrap">
              Save {amountSaved}
            </del>
          )}
        </span>

        {/* RIGHT SIDE: Price details */}
        {pricingDetailsData && (
          <div
            className={`cursor-pointer whitespace-nowrap underline ${pricingDetailsClass}`}
            onClick={handlePriceModalClick}
          >
            Price details
          </div>
        )}
      </span>
    );
  }

  // Show the price and price details link if the price is equal to or greater than the compareAtPrice
  return (
    <span
      className={`flex items-baseline justify-between ${
        pricingDetailsClass?.includes('pdp-price-details') ? '' : ''
      }`}
    >
      {/* LEFT SIDE: Prices */}
      <span className="sr-only">Price is</span>
      <span className={`${salePriceClassName} `}>
        {formatPrice(parsedPrice)}
      </span>
      {/* RIGHT SIDE: Price details */}
      {pricingDetailsData && (
        <div
          className={`cursor-pointer whitespace-nowrap underline ${pricingDetailsClass}`}
          onClick={handlePriceModalClick}
        >
          Price details
        </div>
      )}
    </span>
  );
}

/**
 * @param num The price (as a number)
 * @param outputZeroAsFree Whether to output "FREE" when the price is zero
 * @returns A formatted string of the price, with two decimal places if the
 *   number is not whole.
 */
function formatPrice(num, outputZeroAsFree = true) {
  if (outputZeroAsFree && num <= 0) {
    return 'FREE';
  }

  const formattedPrice = num % 1 === 0 ? num.toString() : num.toFixed(2);
  return `$${formattedPrice}`;
}

function CrossedPrice({ value, className }) {
  return <del className={`mr-1 ${className}`}>{formatPrice(value)}</del>;
}

function SalePrice({ value, className }) {
  return (
    <ins className={`mr-1 ${className} no-underline`}>{formatPrice(value)}</ins>
  );
}
